import React, { useEffect, useState } from 'react';
import { Draggable } from '../../pages/home/Draggable';
import './index.less';

interface OcTrack {
  hasOcImage: boolean;
  id: string;
  hash: string;
  top_color: string;
  bottom_color: string;
  isNew?: boolean;
  link?: string;
  isRecordable?: boolean;
}

interface OcTrackListProps {
  tracks: OcTrack[];
  onTrackClick?: (track: OcTrack) => void;
  onDelete?: (track: OcTrack) => void;
  onTracksReorder?: (tracks: OcTrack[]) => void;
  deleteMode?: boolean;
  recordMode?: boolean;
  addToPlayingTracks?: (track: any) => void;
  photoMode?: boolean;
  lockedHashes?: Set<string>;
  tracksWithCandidates?: Set<string>;
  generatingAnimationHashes?: Set<string>;
}

const darkenColor = (color: string, amount: number = 20): string => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darkerR = Math.max(0, r - amount);
  const darkerG = Math.max(0, g - amount);
  const darkerB = Math.max(0, b - amount);

  return `#${darkerR.toString(16).padStart(2, '0')}${darkerG.toString(16).padStart(2, '0')}${darkerB.toString(16).padStart(2, '0')}`;
};

const TrackItem: React.FC<{
  track: OcTrack;
  index: number;
  onClick?: (track: OcTrack) => void;
  onDelete?: (track: OcTrack) => void;
  deleteMode?: boolean;
  recordMode?: boolean;
  addToPlayingTracks?: (track: any) => void;
  photoMode?: boolean;
  lockedHashes?: Set<string>;
  tracksWithCandidates?: Set<string>;
  generatingAnimationHashes?: Set<string>;
}> = ({ track, index, onClick, onDelete, deleteMode, recordMode, addToPlayingTracks, photoMode, lockedHashes, tracksWithCandidates, generatingAnimationHashes }) => {
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    fetch('/assets/files/origin/original-0-head.svg')
      .then(response => response.text())
      .then(content => {
        const svgMatch = content.match(/<svg[^>]*>[\s\S]*<\/svg>/i);
        if (svgMatch) {
          setSvgContent(svgMatch[0]);
        } else {
          console.error('No SVG content found in the response');
        }
      })
      .catch(error => {
        console.error('Error loading SVG:', error);
      });
  }, []);

  const getSvgStyle = () => {
    if (!track.top_color || !track.bottom_color) return {};
    
    return {
      '--top-color': track.top_color,
      '--bottom-color': track.bottom_color,
      '--top-color-dark': darkenColor(track.top_color),
      '--bottom-color-dark': darkenColor(track.bottom_color),
    } as React.CSSProperties;
  };

  const handleClick = () => {
    console.log('handleClick called with track:', track);

    // 如果音轨被锁止或正在生成动画，禁止点击
    if (lockedHashes?.has(track.hash) || generatingAnimationHashes?.has(track.hash)) {
      return;
    }

    if (deleteMode && onDelete) {
      onDelete(track);
    } else {      
      if (onClick) {
        onClick(track);
      }
    }
  };

  // 处理SVG内容，添加类名以便应用样式
  const processedSvgContent = svgContent.replace(/<svg/, `<svg class="track-svg-inner"`);

  // 构建track的className
  const trackClassName = [
    'track',
    track.isNew ? 'new' : '',
    deleteMode ? 'delete-mode' : '',
    recordMode ? 'record-mode' : '',
    photoMode ? 'photo-mode' : '',
    track.link ? 'has-link' : '',
    (photoMode && !track.hasOcImage) ? 'photoable' : '',
    lockedHashes?.has(track.hash) ? 'locked' : '',
    // 只有在没有锁止且没有生成动画时才显示候选图片的弹跳效果
    (tracksWithCandidates?.has(track.hash) && !lockedHashes?.has(track.hash) && !generatingAnimationHashes?.has(track.hash)) ? 'has-candidates' : '',
    generatingAnimationHashes?.has(track.hash) ? 'generating-animation' : ''
  ].filter(Boolean).join(' ');

  const trackContent = (
    <div className={trackClassName} onClick={handleClick}>
      {track.hasOcImage ? (
        <div className="oc-image">
          <img src={`https://oss.usecubes.cn/incredibox/oc/aianimation/${track.hash}-0.png`} alt="oc-image" />
        </div>
      ) : <div 
        className="track-svg"
        style={getSvgStyle()}
        dangerouslySetInnerHTML={{ __html: processedSvgContent }}
      />}
      
      {/* 锁死状态和生成动画状态的loading遮罩层 */}
      {(lockedHashes?.has(track.hash) || generatingAnimationHashes?.has(track.hash)) && (
        <div className="lock-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {deleteMode || recordMode || !track.link || photoMode || tracksWithCandidates?.has(track.hash) ? (
        <div className={`${deleteMode ? 'deleteTrack' : ''} ${recordMode ? 'recordTrack' : ''} ${!track.link ? 'nolinkTrack' : ''} ${track.isRecordable ? 'recordable' : ''} ${photoMode ? 'photoTrack' : ''} ${(photoMode && !track.hasOcImage) ? 'photoable' : ''} ${(tracksWithCandidates?.has(track.hash) && !lockedHashes?.has(track.hash) && !generatingAnimationHashes?.has(track.hash)) ? 'has-candidates' : ''}`}>
          {trackContent}
        </div>
      ) : (
        <Draggable id={`oc_${index}`}>
          {trackContent}
        </Draggable>
      )}
    </>
  );
};

const OcTrackList: React.FC<OcTrackListProps> = ({
  tracks,
  onTrackClick,
  onDelete,
  onTracksReorder,
  deleteMode = false,
  recordMode = false,
  photoMode = false,
  addToPlayingTracks,
  lockedHashes,
  tracksWithCandidates,
  generatingAnimationHashes
}) => {
  // 确保显示20个轨道，不足的用空轨道填充，超过的截取前20个
  const displayTracks = [...tracks.slice(0, 20), ...Array(20 - Math.min(tracks.length, 20)).fill(null)];

  return (
    <>
      <div className="ocTracksWrap">
        {displayTracks.map((track, index) => (
          track ? (
            <TrackItem
              key={track.id}
              track={track}
              index={index}
              onClick={onTrackClick}
              onDelete={onDelete}
              deleteMode={deleteMode}
              recordMode={recordMode}
              photoMode={photoMode}
              addToPlayingTracks={addToPlayingTracks}
              lockedHashes={lockedHashes}
              tracksWithCandidates={tracksWithCandidates}
              generatingAnimationHashes={generatingAnimationHashes}
            />
          ) : (
            <div key={`empty-${index}`} className="empty-track" />
          )
        ))}
      </div>
    </>
  );
};

export default OcTrackList; 